import { useCallback, useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { Invoice } from 'types';

import { api } from 'api';

const InvoiceActions = ({
  invoice,
  onSuccess,
  onDeleteSuccess,
}: {
  invoice: Invoice;
  onSuccess: () => Promise<void>;
  onDeleteSuccess: () => Promise<void>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { mutateAsync } = api.useMutation('put', '/invoices/{id}');
  const { mutateAsync: deleteMutateAsync } = api.useMutation(
    'delete',
    '/invoices/{id}'
  );

  const handleError = (action: string) => {
    toast.error(`Failed to ${action} invoice. Please try again.`);
  };

  const onFinalize = useCallback(async () => {
    setIsLoading(true);
    try {
      await mutateAsync({
        params: { path: { id: invoice.id } },
        body: { invoice: { finalized: true } },
      });
      await onSuccess();
      toast.success('Invoice finalized.');
    } catch {
      handleError('finalize');
    } finally {
      setIsLoading(false);
    }
  }, [invoice.id, mutateAsync, onSuccess]);

  const onMarkAsPaid = useCallback(async () => {
    setIsLoading(true);
    try {
      await mutateAsync({
        params: { path: { id: invoice.id } },
        body: { invoice: { paid: true } },
      });
      await onSuccess();
      toast.success('Invoice marked as paid.');
    } catch {
      handleError('mark as paid');
    } finally {
      setIsLoading(false);
    }
  }, [invoice.id, mutateAsync, onSuccess]);

  const onDelete = useCallback(async () => {
    setIsLoading(true);
    try {
      await deleteMutateAsync({
        params: { path: { id: invoice.id } },
      });
      await onDeleteSuccess();
      toast.success('Invoice deleted.');
    } catch {
      handleError('delete');
    } finally {
      setIsLoading(false);
    }
  }, [invoice.id, deleteMutateAsync, onDeleteSuccess]);

  return (
    <>
      <Row className="mt-5">
        <Col xs={10}>
          {!invoice.finalized && (
            <Button disabled={isLoading} onClick={onFinalize}>
              {isLoading ? 'Finalizing...' : 'Mark as finalized'}
            </Button>
          )}
          {invoice.finalized && !invoice.paid && (
            <Button disabled={isLoading} onClick={onMarkAsPaid}>
              {isLoading ? 'Marking...' : 'Mark as paid'}
            </Button>
          )}
        </Col>
        {!invoice.finalized && (
          <Col xs={2} className="d-flex justify-content-end">
            <Button
              disabled={isLoading}
              onClick={() => setShowConfirmationModal(true)}
              variant="outline-danger"
            >
              Delete invoice
            </Button>
          </Col>
        )}
      </Row>

      <Modal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this invoice?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmationModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" disabled={isLoading} onClick={onDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InvoiceActions;
